import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api, fmtEUR, fmtDate, MONTHLY_LIMIT } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    X,
    Phone,
    EnvelopeSimple,
    MapPin,
    IdentificationCard,
    Note,
    FilePdf,
    FileXls,
} from "@phosphor-icons/react";
import {
    exportCustomerPDF,
    exportCustomerXLSX,
} from "@/lib/export-customer";
import { toast } from "sonner";

export default function CustomerCardDialog({ open, onClose, customer }) {
    const [transactions, setTransactions] = useState([]);
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshed, setRefreshed] = useState(null);
    const [monthFilter, setMonthFilter] = useState("all"); // "all" or "YYYY-MM"

    useEffect(() => {
        if (!open || !customer) return;
        let active = true;
        setLoading(true);
        Promise.all([
            api.get(`/customers/${customer.id}`),
            api.get(`/customers/${customer.id}/transactions`),
            api.get(`/customers/${customer.id}/sales`),
        ])
            .then(([cRes, txRes, sRes]) => {
                if (!active) return;
                setRefreshed(cRes.data);
                setTransactions(txRes.data);
                setSales(sRes.data || []);
            })
            .catch(() => {})
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [open, customer]);

    // Available months in this customer's sales (sorted desc)
    const monthsAvailable = useMemo(() => {
        const set = new Set();
        for (const s of sales) {
            if (s.month_key) set.add(s.month_key);
        }
        return Array.from(set).sort((a, b) => (a < b ? 1 : -1));
    }, [sales]);

    const filteredSales = useMemo(() => {
        if (monthFilter === "all") return sales;
        return sales.filter((s) => s.month_key === monthFilter);
    }, [sales, monthFilter]);

    if (!customer) return null;
    const c = refreshed || customer;
    const limitSet = c.period_limit_set !== false;
    const limit = limitSet
        ? (c.period_limit ?? c.monthly_limit ?? MONTHLY_LIMIT)
        : 0;
    const remaining = limitSet ? (c.remaining ?? limit) : 0;
    const used = c.used_this_month ?? 0;
    const usedPct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
    const maxed = limitSet && remaining <= 0;
    const warn = limitSet && remaining <= 5 && !maxed;

    const monthLabel = (mk) => {
        if (!mk || mk === "all") return "ΟΛΑ";
        const [y, m] = mk.split("-").map(Number);
        const names = [
            "ΙΑΝ", "ΦΕΒ", "ΜΑΡ", "ΑΠΡ", "ΜΑΪ", "ΙΟΥΝ",
            "ΙΟΥΛ", "ΑΥΓ", "ΣΕΠ", "ΟΚΤ", "ΝΟΕ", "ΔΕΚ",
        ];
        return `${names[m - 1]} ${y}`;
    };


    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                if (!o) onClose();
            }}
        >
            <DialogContent
                className="rounded-none border-2 border-[#0A0A0A] max-w-5xl p-0 gap-0 max-h-[92vh] overflow-y-auto"
                data-testid="customer-card-dialog"
            >
                <DialogHeader className="p-7 border-b-2 border-[#0A0A0A] flex-row items-start justify-between space-y-0">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5F66] mb-1">
                            ΚΑΡΤΕΛΑ ΠΕΛΑΤΗ
                        </div>
                        <DialogTitle className="font-heading text-3xl font-black tracking-tighter text-[#0A0A0A] flex items-center gap-3 flex-wrap">
                            <Badge className="rounded-none bg-[#0A0A0A] text-white font-mono text-base px-3 py-1.5">
                                #{c.code}
                            </Badge>
                            {c.name}
                            {c.no_credit_control && (
                                <span
                                    className="text-xs font-black tracking-wider px-2 py-1 bg-[#FF9500] text-white"
                                    data-testid="vip-badge-card"
                                >
                                    🛡️ VIP — ΧΩΡΙΣ ΕΛΕΓΧΟ
                                </span>
                            )}
                        </DialogTitle>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-[#F4F5F7] transition-colors"
                        data-testid="card-close"
                    >
                        <X size={22} weight="bold" />
                    </button>
                </DialogHeader>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                    {/* Left: Info */}
                    <div className="lg:col-span-2 p-7 space-y-5 border-b lg:border-b-0 lg:border-r border-[#E2E4E9]">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <InfoField icon={IdentificationCard} label="ΑΦΜ" value={c.afm || "—"} mono />
                            <InfoField icon={Phone} label="ΤΗΛΕΦΩΝΟ" value={c.phone || "—"} mono />
                            <InfoField icon={EnvelopeSimple} label="EMAIL" value={c.email || "—"} />
                            <InfoField icon={MapPin} label="ΘΕΣΗ" value={c.position || "—"} />
                        </div>

                        {/* Balance card */}
                        <div
                            className={`border-2 p-5 ${
                                !limitSet
                                    ? "border-[#5C5F66] bg-[#F4F5F7]"
                                    : maxed
                                      ? "border-[#FF3B30] bg-[#FDEAE8]"
                                      : warn
                                        ? "border-[#FFCC00] bg-[#FFF8DD]"
                                        : "border-[#34C759] bg-[#EAF7EE]"
                            }`}
                            data-testid="card-balance"
                        >
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66]">
                                {c.is_vip || c.no_credit_control
                                    ? "ΥΠΟΛΟΙΠΟ ΜΗΝΑ VIP (5.000€)"
                                    : "ΥΠΟΛΟΙΠΟ ΜΗΝΑ"}
                            </div>
                            {!limitSet ? (
                                <>
                                    <div className="font-heading text-2xl font-black tracking-tighter mt-1 text-[#5C5F66] uppercase">
                                        ΧΩΡΙΣ ΟΡΙΟ ΜΗΝΑ
                                    </div>
                                    <div className="text-xs text-[#5C5F66] mt-1">
                                        Δεν έχει οριστεί όριο πίστωσης για τον τρέχοντα μήνα.
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div
                                        className={`font-heading text-5xl font-black tracking-tighter mt-1 ${
                                            maxed
                                                ? "text-[#B11919]"
                                                : warn
                                                  ? "text-[#7A5C00]"
                                                  : "text-[#1F8A3E]"
                                        }`}
                                    >
                                        {fmtEUR(remaining)}
                                    </div>
                                    <div className="text-xs text-[#5C5F66] mt-1">
                                        Χρησιμοποιήθηκαν{" "}
                                        <strong className="text-[#0A0A0A]">{fmtEUR(used)}</strong>{" "}
                                        από {fmtEUR(limit)}
                                    </div>
                                    <div className="mt-3 h-2 bg-white border border-[#E2E4E9]">
                                        <div
                                            className={`h-full ${
                                                maxed
                                                    ? "bg-[#FF3B30]"
                                                    : warn
                                                      ? "bg-[#FFCC00]"
                                                      : "bg-[#002FA7]"
                                            }`}
                                            style={{ width: `${usedPct}%` }}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {c.info && (
                            <div className="border border-[#E2E4E9] p-4">
                                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66] flex items-center gap-2 mb-2">
                                    <Note size={12} weight="bold" /> ΠΛΗΡΟΦΟΡΙΕΣ
                                </div>
                                <div className="text-sm whitespace-pre-wrap text-[#1A1A1A]">
                                    {c.info}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right: QR */}
                    <div className="p-7 flex flex-col items-center justify-start bg-[#F4F5F7]">
                        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66] mb-3">
                            QR / BARCODE
                        </div>
                        <div className="bg-white border border-[#E2E4E9] p-5">
                            <QRCodeSVG
                                value={c.qr_barcode || c.code}
                                size={200}
                                level="M"
                                marginSize={1}
                            />
                        </div>
                        <div className="mt-3 text-center font-mono text-xs text-[#5C5F66] break-all max-w-[200px]">
                            {c.qr_barcode || `#${c.code}`}
                        </div>
                        <div className="mt-4 flex flex-col gap-2 w-[200px]">
                            <Button
                                onClick={() => window.print()}
                                variant="outline"
                                data-testid="card-print-btn"
                                className="rounded-none h-10 border-[#0A0A0A] font-bold"
                            >
                                ΕΚΤΥΠΩΣΗ
                            </Button>
                            <Button
                                onClick={async () => {
                                    if (filteredSales.length === 0) {
                                        toast.error("Καμία κίνηση προς εξαγωγή");
                                        return;
                                    }
                                    try {
                                        await exportCustomerPDF({
                                            customer: c,
                                            sales: filteredSales,
                                            periodLabel:
                                                monthFilter === "all"
                                                    ? "Όλοι οι μήνες"
                                                    : monthLabel(monthFilter),
                                            fileSuffix:
                                                monthFilter === "all"
                                                    ? ""
                                                    : `-${monthFilter}`,
                                        });
                                    } catch {
                                        toast.error("Σφάλμα εξαγωγής PDF");
                                    }
                                }}
                                variant="outline"
                                data-testid="card-export-pdf"
                                className="rounded-none h-10 border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-bold text-xs"
                            >
                                <FilePdf size={14} weight="bold" className="mr-2" />
                                EXPORT PDF
                            </Button>
                            <Button
                                onClick={() => {
                                    if (filteredSales.length === 0) {
                                        toast.error("Καμία κίνηση προς εξαγωγή");
                                        return;
                                    }
                                    try {
                                        exportCustomerXLSX({
                                            customer: c,
                                            sales: filteredSales,
                                            periodLabel:
                                                monthFilter === "all"
                                                    ? "Όλοι οι μήνες"
                                                    : monthLabel(monthFilter),
                                            fileSuffix:
                                                monthFilter === "all"
                                                    ? ""
                                                    : `-${monthFilter}`,
                                        });
                                    } catch {
                                        toast.error("Σφάλμα εξαγωγής Excel");
                                    }
                                }}
                                variant="outline"
                                data-testid="card-export-xlsx"
                                className="rounded-none h-10 border-2 border-[#1F8A3E] text-[#1F8A3E] hover:bg-[#1F8A3E] hover:text-white font-bold text-xs"
                            >
                                <FileXls size={14} weight="bold" className="mr-2" />
                                EXPORT EXCEL
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Transactions */}
                <div className="border-t-2 border-[#0A0A0A]">
                    <div className="px-7 py-4 border-b border-[#E2E4E9] flex items-center justify-between flex-wrap gap-3">
                        <h3 className="font-heading font-black text-lg tracking-tighter">
                            ΙΣΤΟΡΙΚΟ ΧΡΕΩΣΕΩΝ
                        </h3>
                        <div className="flex items-center gap-3 flex-wrap">
                            {monthsAvailable.length > 0 && (
                                <div
                                    className="flex items-center gap-1"
                                    data-testid="card-month-filter"
                                >
                                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                                        ΜΗΝΑΣ:
                                    </span>
                                    <select
                                        value={monthFilter}
                                        onChange={(e) =>
                                            setMonthFilter(e.target.value)
                                        }
                                        data-testid="card-month-select"
                                        className="rounded-none h-8 px-2 border-2 border-[#0A0A0A] bg-white text-xs font-bold uppercase tracking-wider focus:outline-none"
                                    >
                                        <option value="all">ΟΛΟΙ ΟΙ ΜΗΝΕΣ</option>
                                        {monthsAvailable.map((mk) => (
                                            <option key={mk} value={mk}>
                                                {monthLabel(mk)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5F66]">
                                {transactions.length} εγγραφές
                            </span>
                        </div>
                    </div>
                    {loading ? (
                        <div className="p-8 text-center text-sm text-[#5C5F66]">
                            Φόρτωση...
                        </div>
                    ) : transactions.length === 0 ? (
                        <div className="p-8 text-center text-sm text-[#5C5F66]">
                            Δεν υπάρχουν χρεώσεις.
                        </div>
                    ) : (
                        <div className="overflow-x-auto max-h-[300px]">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-b border-[#E2E4E9] hover:bg-transparent">
                                        <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em]">
                                            Ημ/νια
                                        </TableHead>
                                        <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em]">
                                            Μηνας
                                        </TableHead>
                                        <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-right">
                                            Αιτηθεν
                                        </TableHead>
                                        <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-right">
                                            Χρεωθηκε
                                        </TableHead>
                                        <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-right">
                                            Επιπλεον
                                        </TableHead>
                                        <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-right">
                                            Υπολοιπο
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {transactions.map((tx) => (
                                        <TableRow
                                            key={tx.id}
                                            className="border-b border-[#E2E4E9]"
                                        >
                                            <TableCell className="font-mono text-xs">
                                                {fmtDate(tx.created_at)}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">
                                                {tx.month_key}
                                            </TableCell>
                                            <TableCell className="text-right font-semibold text-sm">
                                                {fmtEUR(tx.amount_requested)}
                                            </TableCell>
                                            <TableCell className="text-right font-bold text-sm">
                                                {fmtEUR(tx.amount_charged)}
                                            </TableCell>
                                            <TableCell className="text-right font-bold text-sm">
                                                {tx.overage > 0 ? (
                                                    <span className="text-[#FF3B30]">
                                                        +{fmtEUR(tx.overage)}
                                                    </span>
                                                ) : (
                                                    <span className="text-[#5C5F66]">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-sm">
                                                {fmtEUR(tx.balance_after)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function InfoField({ icon: Icon, label, value, mono }) {
    return (
        <div className="border border-[#E2E4E9] p-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66] flex items-center gap-2 mb-1">
                <Icon size={12} weight="bold" />
                {label}
            </div>
            <div
                className={`text-sm text-[#0A0A0A] break-all ${mono ? "font-mono" : ""}`}
            >
                {value}
            </div>
        </div>
    );
}
